import React, { useState, useEffect } from 'react';
import { Button, Grid, Grid2, Stack } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { auth } from './firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useRole } from '../auth/RoleContext';
import { visiblePlants } from '../auth/permissions';
import SearchSelect from './components/SearchSelect';
import { useT } from '../i18n/LanguageContext';

// Example companies and trucks (replace with dynamic data)
// const companies = ["Santharam Transport LLC", "RED Jay TRUCKING", "VELA"];
//const trucks = ["244", "256", "196"];

const Filters = ({
  onFilterChange,
  ScreenName,
  showPlant = false,
  showDispatcher = false,
  showCarrier = false,
  showDriver = false,
  hideCompany = false,
  hideTruck = false,
  // Weekly screens (e.g. Payroll): picking a start date fills in its whole Monday–Sunday week (never an
  // off-by-one Sun–Sat). The end date can then be changed on its own.
  snapWeek = false,
  // Optional starting values (e.g. to restore a saved search). Dates are ISO yyyy-MM-dd strings;
  // the selects carry the same values handleApplyFilters emits (company id, truck number, plant id…).
  initialFilters,
}) => {

  const init = initialFilters || {};
  const t = useT();

  const getStartOfWeek = (date = new Date()) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
    return new Date(d.setDate(diff));
  };

  const getEndOfWeek = (date = new Date()) => {
    const start = getStartOfWeek(date);
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Sunday
    return end;
  };

  // Parse an ISO yyyy-MM-dd at LOCAL midnight (avoids the UTC day-shift of new Date('2026-07-06')).
  const parseIso = (s) => (s ? new Date(`${s}T00:00:00`) : null);

  // Format a Date as yyyy-MM-dd using its LOCAL calendar day — NOT toISOString() (UTC), which shifts
  // the date forward/back by a day near midnight and made the picked range land on the wrong days.
  const toLocalIso = (d) => {
    if (!d || isNaN(d.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  const [startDate, setStartDate] = useState(parseIso(init.startDate) || getStartOfWeek());
  const [endDate, setEndDate] = useState(parseIso(init.endDate) || getEndOfWeek());

  const [selectedCompany, setSelectedCompany] = useState(init.company || '');
  const [selectedCompanyName, setSelectedCompanyName] = useState(init.companyName || '');
  const [selectedTruck, setSelectedTruck] = useState(init.truck || '');
  const [trucks, setTrucks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [plants, setPlants] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(init.plant || '');
  const [selectedDispatcher, setSelectedDispatcher] = useState(init.dispatcher || '');
  const [selectedCarrier, setSelectedCarrier] = useState(init.carrier || '');
  const [selectedDriver, setSelectedDriver] = useState(init.driver || '');
  const [user] = useAuthState(auth);
  const { role } = useRole();


  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;

        // Replace with your actual API endpoint
        const response = await axios.get(`${apiUrl}/api/invoice/lookups`, {
            params:  {email: user.email}
          });

        setTrucks(response.data.truckNumberLookupList || []);
        setCompanies(response.data.companyLookupList || []);
      } catch (err) {
        console.log(err)
        // setError('Error creating invoice');
        // setLoading(false);
      }
    };

    fetchData();

    // Master lookups (plants/dispatchers/carriers/drivers) — backend TBD.
    // Fail silently so the rest of the screen still works.
    const fetchMasters = async () => {
      if (!showPlant && !showDispatcher && !showCarrier && !showDriver) return;
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/api/master/lookups`, {
          params: { email: user?.email },
        });
        setPlants(response.data?.plants || []);
        setDispatchers(response.data?.dispatchers || []);
        setCarriers(response.data?.carriers || []);
        setDrivers(response.data?.drivers || []);
      } catch (err) {
        // Endpoint may not exist yet; leave selectors empty.
        console.debug('master/lookups unavailable', err?.message);
      }
    };
    fetchMasters();
  }, []);


  // Trucks belonging to the selected company (cascade). No company selected → all trucks.
  const visibleTrucks = selectedCompany
    ? trucks.filter((t) => String(t.companyId) === String(selectedCompany))
    : trucks;

  // Selecting a company clears a truck that no longer belongs to it.
  const handleCompanyChange = (value) => {
    setSelectedCompany(value);
    setSelectedTruck((prev) =>
      trucks.some((t) => t.truckNumber === prev && String(t.companyId) === String(value))
        ? prev
        : ''
    );
  };

  // Function to handle applying filters
  const handleApplyFilters = () => {
    const selectedCompanyObj = companies.find((c) => String(c.id) === String(selectedCompany));
    const filterData = {
      startDate: toLocalIso(startDate),
      endDate: toLocalIso(endDate),
      company: selectedCompany,
      truck: selectedTruck,
      companyName: selectedCompanyObj ? selectedCompanyObj.name : '',
      plant: selectedPlant,
      dispatcher: selectedDispatcher,
      carrier: selectedCarrier,
      driver: selectedDriver,
    };

    if (typeof onFilterChange === 'function') {
      onFilterChange(filterData); // Call the parent function passed as prop
    } else {
      console.error("onFilterChange is not a function");
    }
  };

  // Reset every filter back to its default (this week, all companies/trucks/plants/…) and re-run
  // the search so the results reflect the cleared filters.
  const handleClearFilters = () => {
    const start = getStartOfWeek();
    const end = getEndOfWeek();
    setStartDate(start);
    setEndDate(end);
    setSelectedCompany('');
    setSelectedCompanyName('');
    setSelectedTruck('');
    setSelectedPlant('');
    setSelectedDispatcher('');
    setSelectedCarrier('');
    setSelectedDriver('');

    if (typeof onFilterChange === 'function') {
      onFilterChange({
        startDate: toLocalIso(start),
        endDate: toLocalIso(end),
        company: '', truck: '', companyName: '',
        plant: '', dispatcher: '', carrier: '', driver: '',
      });
    }
  };

  


  // Date change handlers: on weekly screens, picking a start date fills in its whole Mon–Sun week.
  // The end date is always taken as picked — changing it never moves the start date.
  const onStartDate = (v) => {
    if (v && snapWeek) { setStartDate(getStartOfWeek(v)); setEndDate(getEndOfWeek(v)); }
    else setStartDate(v);
  };
  const onEndDate = (v) => setEndDate(v);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid2 size={6}></Grid2>
        <Grid2 size={6}>{ScreenName}</Grid2>
      {/* Phones: no side offset (it pushed the bar off-screen) and full-width fields, one per row. */}
      <Grid container spacing={2} sx={{ mt: { xs: 0, md: '30px' }, mb: { xs: 2, md: '30px' }, ml: { xs: 0, md: '20px' }, width: { xs: '100%', md: 'auto' } }}>
        {/* Start Date */}
        <Grid item xs={12} sm={2}>
          <DatePicker
            label={t('filters.startDate')}
            value={startDate}
            onChange={onStartDate}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>

        {/* End Date */}
        <Grid item xs={12} sm={2}>
          <DatePicker
            label={t('filters.endDate')}
            value={endDate}
            onChange={onEndDate}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>

        {/* Company */}
        {!hideCompany && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.subContractor')} placeholder={t('common.all')}
            options={companies} getOptionLabel={(c) => c.name || ''}
            value={selectedCompany} onChange={handleCompanyChange} />
        </Grid>
        )}

        {/* Truck */}
        {!hideTruck && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.truck')} placeholder={t('common.all')}
            options={visibleTrucks}
            getOptionLabel={(tr) => String(tr.truckNumber ?? '')}
            getOptionValue={(tr) => tr.truckNumber}
            value={selectedTruck} onChange={setSelectedTruck} />
        </Grid>
        )}

        {/* Plant */}
        {showPlant && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.plant')} placeholder={t('common.all')}
            options={visiblePlants(role, plants)}
            getOptionLabel={(p) => p.name || p.code || ''}
            value={selectedPlant} onChange={setSelectedPlant} />
        </Grid>
        )}

        {/* Dispatcher */}
        {showDispatcher && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.dispatcher')} placeholder={t('common.all')}
            options={dispatchers} getOptionLabel={(d) => d.name || ''}
            value={selectedDispatcher} onChange={setSelectedDispatcher} />
        </Grid>
        )}

        {/* Carrier */}
        {showCarrier && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.carrier')} placeholder={t('common.all')}
            options={carriers} getOptionLabel={(c) => c.name || c.code || ''}
            value={selectedCarrier} onChange={setSelectedCarrier} />
        </Grid>
        )}

        {/* Driver */}
        {showDriver && (
        <Grid item xs={12} sm={2}>
          <SearchSelect label={t('filters.driver')} placeholder={t('common.all')}
            options={drivers} getOptionLabel={(d) => d.name || ''}
            value={selectedDriver} onChange={setSelectedDriver} />
        </Grid>
        )}

        {/* Apply / Clear Filters Buttons */}
        <Grid item xs={12} sm="auto" sx={{ mb: { xs: 0, md: '30px' }, ml: { xs: 0, lg: '500px' } }}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              sx={{ flex: { xs: 2, sm: 'none' } }}
            >
              {t('common.search')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearFilters}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              {t('common.clear')}
            </Button>
          </Stack>
        </Grid>

      </Grid>
    </LocalizationProvider>
  );
};

export default Filters;
